import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AssistantToolInfo } from '@cumpli/shared';
import { X, ArrowUp, ArrowUpRight, Square, SquarePen } from 'lucide-react';
import { Thread } from './thread';
import { pageAiActions } from './page-actions';
import { AiSpark } from './tool-cards/spark';
import { SlashMenu, filterTools } from './slash-menu';
import { useAssistantChat } from './use-assistant-chat';
import { useAssistantTools } from '../../lib/hooks';
import { useStickToBottom } from '../../lib/use-stick-to-bottom';

const PROMPTS = [
  'Resumir el riesgo de este cliente',
  '¿Qué documentos faltan?',
  'Validar un nombre en listas',
  'Buscar un cliente',
];

/**
 * Asistente IA slide-over — the surface for the app-wide conversation owned by
 * <AssistantChatProvider> (see use-assistant-chat). This component holds only
 * surface-UI state: the open flag (props), the input box, and scroll position.
 */
export function Assistant({
  open,
  onClose,
  autoAsk = false,
}: {
  open: boolean;
  onClose: () => void;
  autoAsk?: boolean;
}) {
  const { msgs, thinking, retryCount, ask, stop, confirmAction, cancelAction, newConversation } =
    useAssistantChat();
  // Same RBAC-filtered catalog as the /agente page powers the "/" quick-invoke
  // menu here. If it's unavailable the menu simply never opens (slashOpen guards
  // on a non-empty filtered list), so the drawer input keeps working.
  const { data: toolsData } = useAssistantTools();
  const tools = toolsData?.tools ?? [];
  const [input, setInput] = useState('');
  const [active, setActive] = useState(0);
  const [escDismissed, setEscDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageActions = pageAiActions(pathname);
  const lastPath = useRef(pathname);

  // "/" menu — derived purely from the input (mirrors the Agente composer), so it
  // works whether or not a conversation exists.
  const slashQuery = input.startsWith('/') ? input.slice(1) : null;
  const filteredTools = slashQuery !== null ? filterTools(tools, slashQuery) : [];
  const slashOpen = slashQuery !== null && !escDismissed && filteredTools.length > 0;

  // Reset the highlighted row as the "/" query changes.
  useEffect(() => {
    setActive(0);
  }, [slashQuery]);

  // Reliably pin the drawer's own scroll container to the newest message/result.
  // Same robustness as the /agente page (double-rAF + ResizeObserver, respecting a
  // near-bottom threshold), but scoped to this `overflow-y-auto` box (scrollRef).
  // The composer is a footer BELOW the scroll area (not overlapping), so no
  // scroll-margin is needed here — `block: 'end'` on endRef lands cleanly.
  useStickToBottom({
    contentRef,
    endRef,
    scrollRef,
    active: open,
    watch: [msgs, thinking],
    stickHard: msgs[msgs.length - 1]?.role === 'user',
  });

  // Answer cards deep-link into the app via <Link>. This drawer is a modal
  // overlay, so when a card navigates (route changes) while it's open, close it
  // so the user lands on the destination instead of behind the panel. Guarded by
  // the previous path so opening the drawer (no route change) never self-closes.
  useEffect(() => {
    if (lastPath.current !== pathname) {
      lastPath.current = pathname;
      if (open) onClose();
    }
  }, [pathname, open, onClose]);

  // Mirror ask()'s own guard so the input box is cleared only when the message
  // is actually sent — pressing Enter while thinking (the input isn't disabled,
  // only the send button is) must leave the typed text untouched, as before.
  function send(text: string) {
    if (!text.trim() || thinking) return;
    setInput('');
    void ask(text);
  }

  /** Seed the input with a prompt (no auto-send), then focus so the user can edit/send. */
  function seedComposer(prompt: string) {
    setInput(prompt);
    setEscDismissed(false);
    inputRef.current?.focus();
  }

  /** Seed the input with a picked "/" tool's prompt (replacing "/…"; no auto-send). */
  function seedFromTool(tool: AssistantToolInfo) {
    seedComposer(tool.prompt);
  }

  function openEntity(href: string) {
    onClose();
    navigate(href);
  }

  // "Peek → full page": jump to /agente (which shows the SAME active conversation
  // via the shared provider) and close this overlay so the user lands on the page.
  function openInAgente() {
    onClose();
    navigate('/agente');
  }

  // During the guided tour, demonstrate the assistant by asking the first prompt.
  useEffect(() => {
    if (open && autoAsk && msgs.length === 0) {
      const timer = setTimeout(() => void ask(PROMPTS[0]!), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, autoAsk, msgs.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <aside
        data-tour="assistant-panel"
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col overflow-hidden bg-canvas shadow-pop"
      >
        <header className="border-b border-line bg-panel px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10">
              <AiSpark className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-serif text-lg font-medium leading-none text-ink">Cumpli AI</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                Cumplimiento · Ley 23
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink-soft hover:bg-canvas"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={openInAgente}
              className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-canvas px-3 py-1.5 text-xs font-semibold text-primary-dark transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Abrir en Agente
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
            {msgs.length > 0 && (
              <button
                onClick={newConversation}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs font-semibold text-ink-faint transition-colors hover:bg-canvas hover:text-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <SquarePen className="h-3.5 w-3.5" strokeWidth={2} />
                Nueva conversación
              </button>
            )}
          </div>
        </header>

        {/* The scroll container is a fixed-height flex child; the inner wrapper is
            the element whose height grows as content streams in, so the
            ResizeObserver observes it (not the container) to keep the view pinned. */}
        <div ref={scrollRef} className="flex-1 overflow-x-clip overflow-y-auto px-5 py-5">
          <div ref={contentRef} className="space-y-4">
            {msgs.length === 0 && (
              <div className="rounded-2xl border border-line bg-panel p-4 text-sm text-ink-soft">
                Pregúntame lo que quieras sobre cumplimiento. Puedo resumir el riesgo de un cliente,
                listar documentos faltantes, validar un nombre en listas o buscar un cliente — y
                preparar acciones (solicitar documentos, correr screening, actualizar una alerta o
                abrir un expediente) para que las confirmes antes de ejecutarlas.
              </div>
            )}
            <Thread
              msgs={msgs}
              thinking={thinking}
              retryCount={retryCount}
              variant="drawer"
              onConfirmAction={(i, j) => void confirmAction(i, j)}
              onCancelAction={cancelAction}
              onOpenEntity={openEntity}
              onSeedComposer={seedComposer}
            />
            <div ref={endRef} />
          </div>
        </div>

        <div className="border-t border-line bg-panel px-5 py-4">
          {/* Acciones de la página actual: funciones reales del Agente (consulta =
              dispara; acción = propone→confirmas; las que piden un dato se
              siembran en el composer para completarlas). Siempre visibles:
              el drawer sabe DÓNDE estás. */}
          {pageActions.length > 0 && (
            <div className="mb-3">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                En esta página
              </div>
              <div className="flex flex-wrap gap-2">
                {pageActions.map((a) => (
                  <button
                    key={a.label}
                    onClick={() => (a.mode === 'send' ? send(a.prompt) : seedComposer(a.prompt))}
                    disabled={thinking}
                    title={a.prompt}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                      a.kind === 'accion'
                        ? 'border-primary/45 bg-primary/5 text-primary-dark hover:border-primary'
                        : 'border-line-strong bg-canvas text-ink-soft hover:border-primary hover:text-primary-dark'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="relative flex items-center gap-2 rounded-2xl border border-line-strong bg-canvas px-4 py-2.5 focus-within:border-primary">
            {slashOpen && (
              <SlashMenu
                items={filteredTools}
                activeIndex={active}
                onSelect={seedFromTool}
                onActiveChange={setActive}
              />
            )}
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setEscDismissed(false);
              }}
              onKeyDown={(e) => {
                if (slashOpen) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActive((a) => (a + 1) % filteredTools.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((a) => (a - 1 + filteredTools.length) % filteredTools.length);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const tool = filteredTools[active];
                    if (tool) seedFromTool(tool);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setEscDismissed(true);
                  }
                  return;
                }
                if (e.key === 'Enter') send(input);
              }}
              placeholder="Escribe tu pregunta…"
              maxLength={2000}
              className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
            />
            {thinking ? (
              <button
                type="button"
                onClick={stop}
                aria-label="Detener"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white transition-colors hover:bg-primary-dark"
              >
                <Square className="h-3.5 w-3.5 fill-current" strokeWidth={2.5} />
              </button>
            ) : (
              <button
                onClick={() => send(input)}
                disabled={!input.trim()}
                aria-label="Enviar"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white transition-opacity disabled:opacity-40"
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>
          <p className="mt-2 text-center font-mono text-[10px] text-ink-faint">
            Consulta datos reales del tenant · confirma antes de ejecutar
          </p>
        </div>
      </aside>
    </div>
  );
}
